import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_LAST_MINUTE_SLUG } from 'constants/offer'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { NavWrapper } from 'components/OfferPage/styles'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOption, getCheapestAvailablePackageOption, OfferPackageOptionView, arePricesLoading, getFromDurationLabel } from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import config from 'constants/config'
import cx from 'clsx'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import Group from 'components/utils/Group'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'

const Container = styled(NavWrapper)`
  flex: 1;

  &.hideOnMobile.hideOnMobile {
    display: none;
    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`
const Root = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;

  ${mediaQueryUp.tablet} {
    margin-left: auto;
  }

  > * + * {
    margin-left: ${rem(12)};
  }

`

const MobileNewLine = styled.br`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const HiddenTablet = styled.span`
  display: none;

  ${mediaQueryUp.desktop} {
    display: inline;
  }
`

const PriceWrapper = styled(Group)`
  position: relative;
`

const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * + * {
    margin-left: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

interface Props {
  offer: App.Offer;
  onViewPackageOptions?: () => void;
  calendarsLoading?: boolean;
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  headlessMode: boolean;
  durationLabel: string;
}

function LastMinuteOfferPageStickyFooter(props: Props) {
  const {
    offer,
    onViewPackageOptions,
    cheapestSelectedOption,
    cheapestOption,
    calendarsLoading,
    headlessMode,
    durationLabel,
  } = props

  const availableForPurchase = useIsAvailableForPurchase()
  const dispatch = useOfferPageDispatch()

  const displaySoldOutLabel = !availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED
  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const onViewOptionsClick = () => {
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions?.()
  }

  const activePackage = cheapestSelectedOption ?? cheapestOption
  const saleUnit = !cheapestSelectedOption ? offer.saleUnit : 'total'

  return (
    <Container className={cx({ hideOnMobile: hideSoldOutOnMobile })}>
      <Root>
        {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
        {availableForPurchase &&
          <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
            <PriceWrapper direction="horizontal" gap={8} horizontalAlign="end">
              <Group direction="vertical" horizontalAlign="end">
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <PriceRowPrice
                  size="S"
                  price={activePackage.hotelPrice}
                  saleUnit={saleUnit}
                />
                <LoadingIndicator visible={calendarsLoading} floating opaque />
              </Group>
              <TextButton kind="primary" data-testid="offer-view-options" onClick={onViewOptionsClick}>
                View&nbsp;<HiddenTablet>package</HiddenTablet>&nbsp;options
              </TextButton>
            </PriceWrapper>
          </WalledContent>
        }
        {!availableForPurchase && displaySoldOutLabel &&
          <InlineWrapper>
            <BodyText variant="medium" weight="bold" colour="neutral-one">
              This offer has<MobileNewLine /> SOLD OUT
            </BodyText>
            <TextButton kind="primary" to={`/${OFFER_TYPE_LAST_MINUTE_SLUG}`}>
              Browse all Last Minute Escapes
            </TextButton>
          </InlineWrapper>
        }
      </Root>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  calendarsLoading: arePricesLoading(appState, state),
  headlessMode: appState.system.headlessMode,
  durationLabel: getFromDurationLabel(state, appState),
}), LastMinuteOfferPageStickyFooter)
